import React from 'react';
import {
  Alert, Checkbox, ChipSelector, Textarea, useToast,
} from '@parsina/framework';

import { useTranslation } from '../../i18n';
import TaskDialog from '../../components/TaskDialog';
import {
  LOST_REASON_MAX, LOST_REASON_PRESETS, LOST_REASON_STORED, leadStatusKey, lostReasonKey,
} from '../../constants/leadPipeline';
import { crmErrorMessage, crmGql } from '../../lib/crmApi';
import { ARCHIVE_CUSTOMER, SET_LEAD_STATUS } from './queries';

/**
 * The reason that is sent: the preset's label, the free text, or «label: text».
 * Empty when neither was given — which the dialog refuses to submit.
 */
export function composeLostReason(presetLabel, text) {
  const extra = String(text || '').trim();
  if (presetLabel && extra) return `${presetLabel}: ${extra}`;
  return presetLabel || extra;
}

/**
 * «بستن به‌عنوان ازدست‌رفته» — close a lead as lost (§3.2 T8, T9).
 *
 * The reason is REQUIRED: the service refuses `lost` without one, and «a
 * pipeline of lost deals with no reasons is a report nobody can act on». The
 * rep picks a preset («پاسخ نداد», «تکراری», «اسپم/درخواست نامعتبر», …), adds
 * free text, or both; the whole reason is at most 200 characters. The preset is
 * stored as its label in the language the rep is working in.
 *
 * Spam (T9): the rep may also archive the customer record in the same submit.
 * Lost is terminal — the lead becomes read-only — so the dialog says so, and
 * the confirm is destructive and verb-labelled.
 */
export default function LostDialog({
  open, onOpenChange, lead, onDone,
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const [preset, setPreset] = React.useState('');
  const [text, setText] = React.useState('');
  const [archive, setArchive] = React.useState(false);
  const [touched, setTouched] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setPreset(''); setText(''); setArchive(false); setTouched(false); setError(null);
  }, [open]);

  const reason = composeLostReason(preset ? LOST_REASON_STORED[preset] : '', text);
  const tooLong = reason.length > LOST_REASON_MAX;
  const reasonError = touched && !reason
    ? t('demoRequests.lost.reasonRequired')
    : tooLong ? t('demoRequests.lost.tooLong', { max: LOST_REASON_MAX }) : undefined;
  const dirty = !!(preset || text);

  const submit = async () => {
    setTouched(true);
    if (!reason || tooLong || pending) return;
    setPending(true);
    setError(null);
    let closed = false;
    try {
      await crmGql(SET_LEAD_STATUS, { leadId: lead.id, status: 'lost', lostReason: reason });
      closed = true;
      if (preset === 'spam' && archive) {
        await crmGql(ARCHIVE_CUSTOMER, { id: lead.customerId, reason });
      }
      toast.success(t('demoRequests.lost.success'));
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      if (closed) {
        toast.error(t('demoRequests.lost.archiveFailed', { message: crmErrorMessage(e, t) }));
        onOpenChange(false);
        onDone?.();
      } else {
        setError(crmErrorMessage(e, t));
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <TaskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('demoRequests.lost.title')}
      lead={<Alert variant="warning">{t('demoRequests.lost.consequence', { status: t(leadStatusKey('lost')) })}</Alert>}
      error={error}
      pending={pending}
      dirty={dirty}
      submitLabel={t('demoRequests.lost.submit')}
      submitVariant="destructive"
      submitDisabled={tooLong}
      onSubmit={submit}
      data-testid="demo-lost-dialog"
    >
      <div data-testid="demo-lost-presets">
        <ChipSelector
          label={t('demoRequests.lost.presetLabel')}
          value={preset}
          onChange={(v) => { setPreset(v || ''); setTouched(true); }}
          options={LOST_REASON_PRESETS.map((p) => ({ value: p, label: t(lostReasonKey(p)) }))}
          disabled={pending}
        />
      </div>
      <Textarea
        label={preset ? t('demoRequests.lost.detailOptional') : t('demoRequests.lost.detail')}
        value={text}
        onChange={(e) => setText(e.target?.value ?? e)}
        onBlur={() => setTouched(true)}
        maxLength={LOST_REASON_MAX}
        showCount
        rows={3}
        error={reasonError}
        disabled={pending}
        data-testid="demo-lost-reason"
      />
      {preset === 'spam' ? (
        <Checkbox
          checked={archive}
          onChange={(v) => setArchive(v === true)}
          disabled={pending}
          label={t('demoRequests.lost.archive')}
          data-testid="demo-lost-archive"
        />
      ) : null}
    </TaskDialog>
  );
}
